import React, { useState, useEffect } from 'react'

import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Grid,
    FormControlLabel,
    FormGroup,
    Checkbox,
    Radio,
    RadioGroup,
    Box,
    Tooltip,
    Menu,
    MenuItem,
    ButtonGroup,
    ListItemIcon,
    ListItemText,
    CircularProgress,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CheckIcon from '@mui/icons-material/Check'
import { useZustand } from '../../hooks/useZustand'
import { getTopicName, extractSortValue } from '../../hooks/utils'
import AdaptiveButton from '../AdaptiveButton'
import ChangeFilterOverlapToggle from './ChangeFilterOverlapToggle'
import SelectedFilters from './SelectedFilters'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'

import { sendToAnalytics } from '../../Analytics'
import { UserContext } from '../../UserContext'
import {
    useContextsStore,
    selectIsPublicView,
} from '../../stores/useContextsStore'

import { useFilterStore } from '../../stores/useFilterStore'
import { useBootstrapStore } from '../../stores/useBootstrapStore'
import { useSnackbar } from '../Snackbar'

const MergedFilterPanel = ({ forceFullButton = false } = {}) => {
    const availableFilters = useFilterStore(state => state.availableOptions)
    const recalculate = useFilterStore(state => state.recalculate)
    const highlight = useFilterStore(state => state.highlight)

    const tags = availableFilters.tags

    const filterMode = useFilterStore(state => state.filterMode)
    const [recalculateAnalytics, setRecalculateAnalytics] = useState(
        filterMode === 'recalculate'
    )

    const [filterOverlapMode, setFilterOverlapMode] = React.useState(
        filterMode === 'recalculate'
            ? recalculate.overlapMode
            : highlight.overlapMode
    )

    // Sentiment options
    const sentimentOptions = availableFilters.sentiments

    // Percentage of all statements per sentiment, shown in brackets next to each
    // option once Google sentiment analysis has tagged the statements. Uses the
    // same set/denominator as the "% of statements" shown above the statements
    // panel (useFilterStore.statements).
    const allStatementsForSentiment = useFilterStore(state => state.statements)
    const sentimentPercentages = React.useMemo(() => {
        if (
            !allStatementsForSentiment ||
            allStatementsForSentiment.length === 0
        ) {
            return {}
        }
        const total = allStatementsForSentiment.length
        const counts = {}
        let tagged = 0
        allStatementsForSentiment.forEach(statement => {
            if (statement.sentiment) {
                counts[statement.sentiment] =
                    (counts[statement.sentiment] || 0) + 1
                tagged += 1
            }
        })
        if (tagged === 0) return {} // sentiments not calculated yet
        const pct = {}
        Object.keys(counts).forEach(key => {
            pct[key] = Math.round((counts[key] / total) * 100)
        })
        return pct
    }, [allStatementsForSentiment])

    const hasSentimentPercentages = Object.keys(sentimentPercentages).length > 0
    const isAnalyzingSentiment = useZustand(
        state => state.googleSentimentStatus === 'loading'
    )

    const topics = availableFilters.topics
    // State to manage dialog visibility
    const [open, setOpen] = useState(false)

    // Local state to manage selected options before confirming
    const [selectedSentiments, setSelectedSentiments] = useState([])
    const [selectedTags, setSelectedTags] = useState([])
    const [selectedTopics, setSelectedTopics] = useState([])
    const [filterOverlapModeChanged, setFilterOverlapModeChanged] = useState(
        false
    )

    const { username } = React.useContext(UserContext)
    const isPublic = useContextsStore(selectIsPublicView)
    const viewType = useBootstrapStore(state => state.viewType)
    const currentCompareMode = useZustand(state => state.currentCompareMode)
    // Single-context only: compare is still viewType 'context' but multi-bucket,
    // so exclude it via currentCompareMode; synthesis/search aren't 'context'.
    const sentimentFilterEnabled = viewType === 'context' && !currentCompareMode
    const showSnackbar = useSnackbar(state => state.showSnackbar)

    // Size the grid columns + dialog width to however many columns actually
    // render, so the modal doesn't leave an empty gap when one is hidden.
    const visibleColumnCount =
        (sentimentFilterEnabled ? 1 : 0) +
        (tags.length > 0 ? 1 : 0) +
        (topics && topics.length > 0 ? 1 : 0)
    const columnMd =
        visibleColumnCount > 1 ? Math.floor(12 / visibleColumnCount) : 12
    const dialogMaxWidth = visibleColumnCount >= 3 ? 'md' : 'sm'

    // highlight mode only:
    // ensure the selected options are in sync with current highlight filter
    // and the overlap mode is set to the current filter

    useEffect(() => {
        if (open && !recalculateAnalytics) {
            setSelectedSentiments(highlight.sentiments)
            setSelectedTags(highlight.tags)
            setSelectedTopics(highlight.topics)
        }
        if (open) {
            setFilterOverlapMode(
                recalculateAnalytics
                    ? recalculate.overlapMode
                    : highlight.overlapMode
            )
        }
    }, [open])

    const clearLocalState = () => {
        setSelectedSentiments([])
        setSelectedTags([])
        setSelectedTopics([])
    }
    const handleOpen = () => {
        setOpen(true)
    }
    const handleClose = () => {
        setOpen(false)
    }

    const handleSwitchOverlapClick = () => {
        setFilterOverlapMode(!filterOverlapMode)
        setFilterOverlapModeChanged(true)
        useFilterStore
            .getState()
            .changeOverlap(recalculateAnalytics ? 'recalculate' : 'highlight')

        sendToAnalytics({
            userId: username,
            event: 'statements_filter_action',
            data: {
                action: 'change_overlap_mode',
                filterMode: recalculateAnalytics ? 'recalculate' : 'highlight',
                mode: filterOverlapMode ? 'overlap' : 'union',
                source: 'filter_panel',
            },
        })

        if (typeof posthog != 'undefined') {
            posthog.capture('statements_filter_action', {
                action: 'change_overlap_mode',
            })
        }
    }

    const handleSentimentChange = event => {
        const { value } = event.target
        // Single-select: only one sentiment can be active at a time, so the
        // newly picked value replaces whatever was selected before.
        setSelectedSentiments([value])

        // Selecting a sentiment only makes sense in recalculate mode, so
        // automatically turn on the "Filter & Recalculate Analysis" panel.
        if (!recalculateAnalytics) {
            setRecalculateAnalytics(true)
        }
    }

    // Run Google sentiment analysis on demand (when no sentiment % is available
    // yet) so the per-option percentages can be shown without applying a filter.
    const handleCalculateSentiment = async () => {
        if (useZustand.getState().googleSentimentStatus === 'loading') {
            showSnackbar('info', 'Still analysing sentiment…', 'bottom', 60000)
            return
        }
        showSnackbar(
            'info',
            'Analyzing sentiment using Google AI for high-grade results… This may take up to 60 seconds.',
            'bottom',
            60000
        )
        try {
            const reassessed = await useZustand
                .getState()
                .loadGoogleSentiments({ user: username, isPublic })
            useFilterStore.getState().setFilteredStatements(reassessed)
            showSnackbar('success', 'Sentiment analysis ready', 'bottom')
        } catch (err) {
            showSnackbar(
                'error',
                "Couldn't analyse sentiment — try again",
                'bottom'
            )
        }
    }

    const handleTagChange = event => {
        const { value, checked } = event.target
        setSelectedTags(prev =>
            checked ? [...prev, value] : prev.filter(item => item !== value)
        )

        sendToAnalytics({
            userId: username,
            event: 'statements_filter_action',
            data: {
                action: 'tag_filter_click',
                filterMode: recalculateAnalytics ? 'recalculate' : 'highlight',
                source: 'filter_panel',
            },
        })

        if (typeof posthog != 'undefined') {
            posthog.capture('statements_filter_action', {
                action: 'tag_filter_click',
            })
        }
    }

    const handleTopicChange = event => {
        const { value, checked } = event.target
        // const topicInfo = useZustand.getState().topics.find(topic => topic.id === value)
        const topic = useZustand
            .getState()
            .topics.find(topic => getTopicName(topic) === value)
        if (checked) {
            setSelectedTopics(prev => [...prev, topic])
        } else {
            setSelectedTopics(prev =>
                prev.filter(topic => getTopicName(topic) !== value)
            )
        }

        sendToAnalytics({
            userId: username,
            event: 'statements_filter_action',
            data: {
                action: 'topic_filter_click',
                filterMode: recalculateAnalytics ? 'recalculate' : 'highlight',
                source: 'filter_panel',
            },
        })
        if (typeof posthog != 'undefined') {
            posthog.capture('statements_filter_action', {
                action: 'topic_filter_click',
            })
        }
    }

    // Apply the currently-selected filters. `sentiments` is passed explicitly so
    // the caller can apply the full set (on success) or drop sentiment (on error).
    // `useRecalculate` is passed explicitly (rather than read from state) because
    // the Apply menu sets the mode and applies in the same tick — state would be
    // stale here.
    const applyFilter = async (sentiments, useRecalculate) => {
        if (useRecalculate) {
            const newFilter = {
                // Sentiment is single-select: a newly picked sentiment replaces
                // the previously applied one instead of stacking on top of it.
                // When none is picked, keep whatever sentiment is already applied.
                sentiments:
                    sentiments.length > 0 ? sentiments : recalculate.sentiments,
                tags: [...recalculate.tags, ...selectedTags],
                topics: [...recalculate.topics, ...selectedTopics],
                overlapMode: filterOverlapMode,
            }
            // await so the "ready" toast fires after the recalc actually completes.
            await useFilterStore.getState().setRecalculate(newFilter)
            useFilterStore.getState().setFilterMode('recalculate')
            clearLocalState()
        } else {
            const newFilter = {
                sentiments,
                tags: selectedTags,
                topics: selectedTopics,
                overlapMode: filterOverlapMode,
            }
            useFilterStore.getState().setHighlight(newFilter)
        }
    }

    const reportApply = useRecalculate => {
        sendToAnalytics({
            userId: username,
            event: 'statements_filter_action',
            data: {
                action: 'apply_filter',
                filterMode: useRecalculate ? 'recalculate' : 'highlight',
                source: 'filter_panel',
                mode: 'sentiment_analysis',
            },
        })
        sendToAnalytics({
            userId: username,
            event: 'analytics_event',
            data: {
                action: 'sentiment_analytics',
                filterMode: useRecalculate ? 'recalculate' : 'highlight',
                source: 'filter_panel',
            },
        })
        if (typeof posthog != 'undefined') {
            posthog.capture('statements_filter_action', {
                action: 'apply_filter',
                mode: 'sentiment_analysis',
            })
        }
    }

    // Apply split-button: the main button applies the last-chosen mode; the
    // chevron opens a menu to switch between Highlight and Filter. The choice is
    // remembered (localStorage) so it persists across opens and reloads.
    // applyMode === true → Filter (recalculate), false → Highlight.
    const APPLY_MODE_KEY = 'mergedFilterApplyMode'
    const readApplyMode = () => {
        try {
            return localStorage.getItem(APPLY_MODE_KEY) === 'filter'
        } catch (e) {
            return false
        }
    }
    const persistApplyMode = mode => {
        try {
            localStorage.setItem(APPLY_MODE_KEY, mode ? 'filter' : 'highlight')
        } catch (e) {
            /* ignore unavailable storage */
        }
    }
    const [applyMode, setApplyMode] = useState(readApplyMode)
    const [applyMenuAnchor, setApplyMenuAnchor] = useState(null)
    const handleApplyMenuOpen = event => setApplyMenuAnchor(event.currentTarget)
    const handleApplyMenuClose = () => setApplyMenuAnchor(null)
    // Main button: apply using the remembered mode.
    const handleApplyClick = () => handleConfirm(applyMode)
    // Menu: remember the new mode, then apply with it immediately.
    const handleApplyChoice = useRecalculate => {
        setApplyMode(useRecalculate)
        persistApplyMode(useRecalculate)
        setApplyMenuAnchor(null)
        handleConfirm(useRecalculate)
    }

    const handleConfirm = async (useRecalculate = recalculateAnalytics) => {
        // Keep the toggle state in sync with the chosen mode so the rest of the
        // component (overlap toggle, sync-on-open, etc.) reflects it.
        setRecalculateAnalytics(useRecalculate)

        // Overlap-only change was already applied via changeOverlap(); just close.
        if (filterOverlapModeChanged) {
            setFilterOverlapModeChanged(false)
            setOpen(false)
            return
        }

        setFilterOverlapModeChanged(false)
        setOpen(false)

        const hasSentiment = selectedSentiments.length > 0
        const ctx = useZustand.getState().currentContext
        const needsLoad =
            hasSentiment &&
            useZustand.getState().googleSentimentLoadedFor !== ctx

        if (!needsLoad) {
            await applyFilter(selectedSentiments, useRecalculate)
            reportApply(useRecalculate)
            return
        }

        // Re-Apply mid-flight must not start a second ~30s Google fetch; the
        // in-flight one will land.
        if (useZustand.getState().googleSentimentStatus === 'loading') {
            showSnackbar('info', 'Still analysing sentiment…', 'bottom', 60000)
            return
        }

        // Toast while the ~30s google call runs (modal already closed).
        showSnackbar(
            'info',
            'Analyzing sentiment using Google AI for high-grade results… This may take up to 60 seconds.',
            'bottom',
            60000
        )
        try {
            const reassessed = await useZustand
                .getState()
                .loadGoogleSentiments({ user: username, isPublic })
            // Caller owns the filter-store sync (loadGoogleSentiments doesn't).
            useFilterStore.getState().setFilteredStatements(reassessed)
            await applyFilter(selectedSentiments, useRecalculate)
            showSnackbar('success', 'Sentiment analysis ready', 'bottom')
        } catch (err) {
            // On failure (incl. all-null scores) still apply tags/topics.
            await applyFilter([], useRecalculate)
            showSnackbar(
                'error',
                "Couldn't analyse sentiment — try again",
                'bottom'
            )
        }
        reportApply(useRecalculate)
    }

    // Determine if Confirm button should be disabled
    const noFilterSelected =
        selectedSentiments.length === 0 &&
        selectedTags.length === 0 &&
        selectedTopics.length === 0

    const activeFilterCount = useFilterStore.getState().activeFilterCount()
    // TODOOLEG remove if not needed
    let tagFiltersActive
    let onlyTagsFiltersActive
    let severalTopicsFiltered
    if (recalculateAnalytics) {
        tagFiltersActive =
            recalculate.tags && recalculate.tags.length > 0 ? true : false

        onlyTagsFiltersActive =
            recalculate.topics.length === 0 &&
            recalculate.sentiments.length === 0 &&
            tagFiltersActive
        severalTopicsFiltered = recalculate.topics.length > 1 ? true : false
    } else {
        tagFiltersActive =
            highlight.tags && highlight.tags.length > 0 ? true : false

        onlyTagsFiltersActive =
            highlight.topics.length === 0 &&
            highlight.sentiments.length === 0 &&
            tagFiltersActive

        severalTopicsFiltered = highlight.topics.length > 1 ? true : false
    }

    if (Object.keys(availableFilters).length === 0) {
        return null
    }
    return (
        <>
            {/* Filter Button */}
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                id="categoryFilterButton"
                gap={1}
                sx={{
                    maxWidth: {
                        xs: '30vw', // 600px and less
                        sm: '35vw', // 900px and less
                        md: '40vw', // default
                    },
                }}
            >
                <AdaptiveButton
                    onClick={handleOpen}
                    buttonLabel="Filter By"
                    tooltipTitle="Highlight & Filter your graph"
                    iconName="FilterListIcon"
                    forceFullButton={forceFullButton}
                />
            </Box>

            {/* Filter Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth={dialogMaxWidth}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                    }}
                >
                    Choose Filters to Apply
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        maxHeight: '60vh',
                        minHeight: '60vh',
                        overflow: 'auto',
                    }}
                >
                    {activeFilterCount > 0 && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            mb={2}
                            mt={2}
                        >
                            <Box
                                display="flex"
                                alignItems="baseline"
                                justifyContent="start"
                            >
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    sx={{ fontWeight: 'bold', mr: 1 }}
                                >
                                    Currently Selected Filters
                                </Typography>
                                <Chip
                                    label={
                                        filterMode === 'highlight'
                                            ? 'Highlight mode'
                                            : 'Filter & Recalculate mode'
                                    }
                                    color={
                                        filterMode === 'highlight'
                                            ? 'primary'
                                            : 'secondary'
                                    }
                                    variant="outlined"
                                />
                            </Box>

                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <SelectedFilters />
                            </Box>

                            <Box
                                sx={{
                                    mt: 1,
                                    ml: 0.5,
                                }}
                            >
                                <ChangeFilterOverlapToggle
                                    activeFilterCount={activeFilterCount}
                                    shouldDisableFilter={severalTopicsFiltered}
                                    handleSwitchClick={handleSwitchOverlapClick}
                                />
                            </Box>

                            <Divider variant="fullWidth" sx={{ my: 2 }} />
                        </Box>
                    )}

                    {/* Display loading indicator or error message if needed */}
                    {/* {topics.length === 0 ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            my={2}
                        >
                            <CircularProgress />
                        </Box>
                    ) : ( */}
                    <Grid container spacing={4}>
                        {/* Sentiment Filter — single-context views only */}
                        {sentimentFilterEnabled && (
                            <Grid item xs={12} md={columnMd}>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Sentiment
                                </Typography>
                                <RadioGroup
                                    value={selectedSentiments[0] || ''}
                                    onChange={handleSentimentChange}
                                >
                                    {sentimentOptions.map(option =>
                                        !option ? null : (
                                            <FormControlLabel
                                                key={option}
                                                control={<Radio />}
                                                value={option}
                                                label={
                                                    sentimentPercentages[
                                                        option
                                                    ] != null
                                                        ? `${option} (${sentimentPercentages[option]}%)`
                                                        : option
                                                }
                                            />
                                        )
                                    )}
                                </RadioGroup>
                                {!hasSentimentPercentages && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleCalculateSentiment}
                                        disabled={isAnalyzingSentiment}
                                        startIcon={
                                            isAnalyzingSentiment ? (
                                                <CircularProgress
                                                    size={14}
                                                    color="inherit"
                                                />
                                            ) : null
                                        }
                                        sx={{ mt: 1, textTransform: 'none' }}
                                    >
                                        {isAnalyzingSentiment
                                            ? 'Analyzing sentiment…'
                                            : 'Calculate sentiment'}
                                    </Button>
                                )}
                            </Grid>
                        )}

                        {/* Tags Filter */}
                        {tags.length > 0 ? (
                            <Grid item xs={12} md={columnMd}>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Tags
                                </Typography>

                                <FormGroup>
                                    {tags
                                        .sort((a, b) =>
                                            extractSortValue(a).localeCompare(
                                                extractSortValue(b)
                                            )
                                        )
                                        .map(tag =>
                                            !tag ? null : (
                                                <FormControlLabel
                                                    key={tag}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedTags.includes(
                                                                tag
                                                            )}
                                                            onChange={
                                                                handleTagChange
                                                            }
                                                            value={tag}
                                                        />
                                                    }
                                                    label={tag}
                                                />
                                            )
                                        )}
                                </FormGroup>
                            </Grid>
                        ) : null}

                        {/* Topics Filter */}
                        {topics && topics.length > 0 ? (
                            <Grid item xs={12} md={columnMd}>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Topics
                                </Typography>

                                <FormGroup>
                                    {topics.map(topic => {
                                        const topicName = topic
                                            ? getTopicName(topic)
                                            : topic.id
                                        return (
                                            <FormControlLabel
                                                key={topicName}
                                                control={
                                                    <Checkbox
                                                        checked={selectedTopics
                                                            .map(topic =>
                                                                getTopicName(
                                                                    topic
                                                                )
                                                            )
                                                            .includes(
                                                                topicName
                                                            )}
                                                        onChange={
                                                            handleTopicChange
                                                        }
                                                        value={topicName}
                                                    />
                                                }
                                                label={topicName}
                                            />
                                        )
                                    })}
                                </FormGroup>
                            </Grid>
                        ) : null}
                    </Grid>
                </DialogContent>

                {/* Dialog Actions */}
                <DialogActions>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            minWidth: '310px',
                            flexDirection: 'row-reverse',
                        }}
                    >
                        <ButtonGroup
                            variant="contained"
                            color="primary"
                            disabled={
                                noFilterSelected && !filterOverlapModeChanged
                            }
                        >
                            <Button onClick={handleApplyClick}>
                                {applyMode
                                    ? 'Filter in Graph'
                                    : 'Highlight in Graph'}
                            </Button>
                            <Button
                                size="small"
                                aria-label="change apply mode"
                                onClick={handleApplyMenuOpen}
                                sx={{ px: 0.5 }}
                            >
                                <KeyboardArrowDownIcon />
                            </Button>
                        </ButtonGroup>
                        <Menu
                            anchorEl={applyMenuAnchor}
                            open={Boolean(applyMenuAnchor)}
                            onClose={handleApplyMenuClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem
                                selected={!applyMode}
                                onClick={() => handleApplyChoice(false)}
                            >
                                <ListItemIcon>
                                    {!applyMode && (
                                        <CheckIcon fontSize="small" />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary="Highlight in Graph"
                                    secondary="Mark matching statements in existing graph"
                                />
                            </MenuItem>
                            <MenuItem
                                selected={applyMode}
                                onClick={() => handleApplyChoice(true)}
                            >
                                <ListItemIcon>
                                    {applyMode && (
                                        <CheckIcon fontSize="small" />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary="Filter in Graph"
                                    secondary="Recalculate the graph from matching statements"
                                />
                            </MenuItem>
                        </Menu>
                        <Button onClick={handleClose} variant="outlined">
                            Cancel
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default MergedFilterPanel
